import { Statut } from "./statut";
import { RoleEntite } from "./role";

/**
 * Represents an employee in the system.
 */
export interface EmployeEntite {
    /**
     * Unique identifier for the employee.
     */
    employeId?: number;

    /**
     * Last name (surname) of the employee.
     */
    nom?: string;

    /**
     * First name of the employee.
     */
    prenom?: string;

    /**
     * Initials for the employee (derived from the name).
     */
    initiales?: string;

    /**
     * Professionnal email address of the employee.
     */
    courriel?: string;

    /**
     * Cell phone number of the employee.
     */
    cellulaire?: string;

    /**
     * Username used for CTRL access.
     */
    usagerCtrl?: string;

    /**
     * Internal employee number.
     */
    noEmploye?: string;

    /**
     * Indicates if the employee is a system account or process user.
     */
    estSysteme?: boolean;

    /**
     * Identifier of the category to which the employee belongs.
     */
    categorieId?: number;

    /**
     * Concatenated 'firstName lastName' string for display.
     * Frontend only field.
     */
    prenomNom?: string;

    /**
     * Combined search string (prenom + nom + initiales + noEmploye) used by tables
     * to filter an employee by name or initials with a single column filter.
     * Frontend only field.
     */
    recherche?: string;

    /**
     * List of IDs of locations for which the employee is responsible.
     */
    emplacementIdsResponsable?: number[];

    /**
     * List of IDs of locations where the employee is a "CP" (Contact Person).
     */
    emplacementIdsCP?: number[];

    /**
     * Initials for CTRL purposes.
     */
    initialesCtrl?: string;

    /**
     * Specifies if the employee has a mechanical indicator/qualification.
     */
    indMecanique?: boolean;

    /**
     * @deprecated This property is deprecated and should not be used.
     * Indicates if the employee is registered.
     */
    indInscrit?: boolean;

    /**
     * Whether to show a preview of the items photo when hovering over the main image in lists.
     */
    indVoirPhotoSurSurvole?: boolean;

    /**
     * UI theme settings specified by this employee.
     */
    theme?: string;

    /**
     * Indicates if the employee's real location should be synchronized with the division in the Hamel Portal.
     */
    indAjouteAuChantier?: boolean;

    /**
     * When false, bons de commande modified by this employee are only marked
     * "pending sync" — a manual CTRL sync is required to push them.
     */
    indSyncAutoBonCommande?: boolean;

    /**
     * When false, the colored canvas washes behind glass surfaces are hidden.
     */
    indCouleursFond?: boolean;

    /**
     * Employment status of the employee.
     */
    statut: Statut;

    /**
     * List of roles assigned to this employee.
     */
    roles?: RoleEntite[];
}

/**
 * Computes the initials of an employee from first and last name (first letter of each).
 * Returns undefined when either part is missing.
 */
export function calculerInitialesEmploye(prenom?: string, nom?: string): string | undefined {
    if (!prenom || !nom) {
        return undefined;
    }
    return `${prenom[0]}${nom[0]}`;
}

/**
 * Enriches an employee with derived search fields (initiales fallback, prenomNom, recherche)
 * so tables and dropdowns can filter by name or initials.
 * Returns the same instance for chaining.
 */
export function enrichirRechercheEmploye<T extends EmployeEntite>(employe?: T): T {
    if (!employe) {
        return employe;
    }
    if (!employe.initiales) {
        employe.initiales = calculerInitialesEmploye(employe.prenom, employe.nom);
    }
    if (!employe.prenomNom && (employe.prenom || employe.nom)) {
        employe.prenomNom = `${employe.prenom ?? ''} ${employe.nom ?? ''}`.trim();
    }
    employe.recherche = [employe.prenom, employe.nom, employe.prenomNom, employe.initiales, employe.noEmploye]
        .filter(x => !!x)
        .join(' ');
    return employe;
}